import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL =
  import.meta.env.VITE_WORKER_API_URL ||
  'https://clarity-platform-api.ryan-dc4.workers.dev';

export function Landing() {
  const [params] = useSearchParams();
  const welcome = params.get('welcome') === '1';
  const email = params.get('email');

  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const data = new FormData(e.currentTarget);
    const emailVal = String(data.get('email') ?? '');
    try {
      const res = await fetch(`${API_URL}/auth/request-link`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: emailVal }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `${res.status}`);
      }
      setSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'unknown error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <p className="text-sm uppercase tracking-widest text-sky-400 text-center">
          Clarity Workspace
        </p>
        {welcome ? (
          <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900/60 p-6 text-center">
            <h1 className="text-2xl font-semibold">Welcome to Clarity.</h1>
            <p className="mt-3 text-slate-400">
              We sent a login link to{' '}
              {email ? (
                <strong className="text-slate-200">{email}</strong>
              ) : (
                'your email'
              )}
              . Open it to enter your workspace.
            </p>
            <p className="mt-4 text-xs text-slate-500">
              Didn&apos;t get it? Check spam, then come back and request another one.
            </p>
          </div>
        ) : sent ? (
          <div className="mt-6 rounded-lg border border-emerald-700/40 bg-emerald-900/10 p-6 text-center">
            <h1 className="text-2xl font-semibold">Check your email.</h1>
            <p className="mt-3 text-slate-400">
              We sent a login link. It expires in 15 minutes.
            </p>
          </div>
        ) : (
          <form className="mt-6 space-y-3" onSubmit={handleSubmit}>
            <h1 className="text-2xl font-semibold text-center">Log in</h1>
            <input
              type="email"
              required
              name="email"
              placeholder="you@example.com"
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder-slate-500 focus:border-sky-400 focus:outline-none"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-sky-500 hover:bg-sky-400 disabled:opacity-50 px-4 py-3 font-semibold text-slate-950"
            >
              {submitting ? 'Sending…' : 'Email me a login link'}
            </button>
            {error && (
              <p className="text-sm text-red-400 text-center">{error}</p>
            )}
            <p className="text-xs text-slate-500 text-center">
              Don&apos;t have a workspace yet?{' '}
              <a href="https://clarity.app/signup" className="text-sky-400 hover:underline">
                Start with Pilot — $149/mo
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
